import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    IconButton,
    TextField,
    InputAdornment,
} from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext";
import "./PaymentModule.css";

function Payments() {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [filterType] = useState("all"); // filterType state is kept if future use is required

    const [currentPage, setCurrentPage] = useState(0);
    const [paymentsPerPage, setPaymentsPerPage] = useState(10);

    // API fetch function
    const fetchPayments = async (search = "") => {
        try {
            setLoading(true);
            const startTime = Date.now();

            const res = await getData(API_ENDPOINTS.getOwnerPayments, { search });

            setPayments(res.result.transactions_history);
            setSummary(res.result);

            // minimum 3s loading
            const elapsed = Date.now() - startTime;
            const remaining = 3000 - elapsed;
            if (remaining > 0) {
                setTimeout(() => setLoading(false), remaining);
            } else {
                setLoading(false);
            }
        } catch (err) {
            console.error("Error fetching payments:", err);
            setLoading(false);
        }
    };

    // initial load
    useEffect(() => {
        fetchPayments();
    }, []);

    // Summary values
    const totalEarnings = summary?.totalRevenue ?? 0;
    const totalPlatformRevenue = Number(summary?.platformRevenue ?? 0);
    const totalCOD = Number(summary?.totalCashOnDelivery ?? 0);
    const totalDebits = Number(summary?.totalDebitsAmount ?? 0);
    const totalCredits = Number(summary?.totalCreditAmount ?? 0);

    // Filter payments
    const filteredPayments = useMemo(() => {
        let currentPayments = payments;

        if (filterType !== "all") {
            currentPayments = currentPayments.filter(
                (payment) => payment.transaction_type === filterType
            );
        }

        return currentPayments;
    }, [payments, filterType]);

    // Pagination
    const paginatedPayments = useMemo(() => {
        const startIndex = currentPage * paymentsPerPage;
        return filteredPayments.slice(startIndex, startIndex + paymentsPerPage);
    }, [filteredPayments, currentPage, paymentsPerPage]);

    const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);

    const handlePaymentsPerPageChange = (event) => {
        setPaymentsPerPage(Number(event.target.value));
        setCurrentPage(0);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    // Search handlers
    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            fetchPayments(searchTerm.trim());
        }
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        fetchPayments("");
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // if cleared with backspace
        if (value === "") {
            fetchPayments("");
        }
    };

    return (
        <>
            <LoadingSpinner isLoading={loading} />

            <Box className="payment-module">
                <Typography variant="h4" component="h1" gutterBottom className="title">
                    Payments
                </Typography>

                {/* Summary Cards */}
                <Box className="summary-cards-container">
                    <Box className="total-earnings-card summary-card">
                        <Typography variant="body1" className="card-title">
                            Total Earnings
                        </Typography>
                        <Typography variant="h5" className="card-amount earnings-amount">
                            ₹{totalEarnings.toFixed(2)}
                        </Typography>
                    </Box>

                    <Box className="total-credits-card summary-card">
                        <Typography variant="body1" className="card-title">
                            Total Platform Revenue
                        </Typography>
                        <Typography variant="h5" className="card-amount credits-amount">
                            ₹{totalPlatformRevenue.toFixed(2)}
                        </Typography>
                    </Box>

                    <Box className="total-debits-card summary-card">
                        <Typography variant="body1" className="card-title">
                            Total Cash On Delivery
                        </Typography>
                        <Typography variant="h5" className="card-amount debits-amount">
                            ₹{totalCOD.toFixed(2)}
                        </Typography>
                    </Box>

                    <Box className="total-debits-amount summary-card">
                        <Typography variant="body1" className="card-title">
                            Total Debits
                        </Typography>
                        <Typography variant="h5" className="card-amount debits-amount">
                            ₹{totalDebits.toFixed(2)}
                        </Typography>
                    </Box>

                    <Box className="total-credits-amount summary-card">
                        <Typography variant="body1" className="card-title">
                            Total Credits
                        </Typography>
                        <Typography variant="h5" className="card-amount credits-amount">
                            ₹{totalCredits.toFixed(2)}
                        </Typography>
                    </Box>
                </Box>

                {/* Payment History Header */}
                <Box className="payment-history-header">
                    <Typography variant="h5" component="h2" sx={{ mb: 2 }} className="payment-history-title">
                        Payment History
                    </Typography>
                    <Box className="controls-right">
                        <TextField
                            placeholder="Search..."
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            InputProps={{
                                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleClearSearch} size="small">
                                            <CloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: "8px" },
                            }}
                            className="search-field"
                        />
                    </Box>
                </Box>

                {/* Table */}
                <Paper elevation={0} className="payment-history-paper">
                    <TableContainer>
                        <Table sx={{ minWidth: 650 }} aria-label="payment history table">
                            <TableHead>
                                <TableRow className="table-header-row">
                                    <TableCell className="table-header-title">Trip ID</TableCell>
                                    <TableCell className="table-header-title">Owner</TableCell>
                                    <TableCell className="table-header-title">Date</TableCell>
                                    <TableCell className="table-header-title">Amount</TableCell>
                                    <TableCell className="table-header-title">Amount Paid</TableCell>
                                    <TableCell className="table-header-title">Type</TableCell>
                                    <TableCell className="table-header-title">Method</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedPayments.length > 0 ? (
                                    paginatedPayments.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.trip_id ?? "N/A"}</TableCell>
                                            <TableCell>{row.owner_name ?? "N/A"}</TableCell>
                                            <TableCell>
                                                {new Date(row.createdAt).toLocaleDateString("en-IN", {
                                                    day: "2-digit",
                                                    month: "short",
                                                    year: "numeric",
                                                })}
                                            </TableCell>
                                            <TableCell>₹{parseFloat(row.total_amount).toFixed(2)}</TableCell>
                                            <TableCell>₹{parseFloat(row.payout_amount).toFixed(2)}</TableCell>
                                            <TableCell>
                                                <Typography className={`type-${row.transaction_type.toLowerCase()}`}>
                                                    {row.transaction_type}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{row.payment_method}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No payment history found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <Box className="pagination-controls">
                        <div className="items-per-page">
                            <Select
                                value={paymentsPerPage}
                                onChange={handlePaymentsPerPageChange}
                                displayEmpty
                                inputProps={{ "aria-label": "items per page" }}
                                className="items-per-page-select"
                            >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                            </Select>
                            <Typography variant="body2" sx={{ mr: 2 }} className="pagination-info">
                                per page
                            </Typography>
                        </div>
                        <Typography variant="body2" sx={{ mr: 2 }} className="pagination-info">
                            {currentPage + 1} of {totalPages} pages
                        </Typography>
                        <IconButton onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0}>
                            <KeyboardArrowLeft />
                        </IconButton>
                        <IconButton
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages - 1}
                        >
                            <KeyboardArrowRight />
                        </IconButton>
                    </Box>
                </Paper>
            </Box>
        </>
    );
}

export default Payments;
